/**
 * The alert card IS the alert form.
 *
 * A proposal from Ask arrives prefilled and every field is the product's own
 * control — the same measures, filters, condition and destination the Alerts
 * page offers — so what the reviewer approves is what gets created, and a
 * channel is never guessed at: it is chosen here, on the card. If Slack is
 * not connected the card says so and offers to connect it, or a webhook.
 *
 * Alerts own their rules; this reads the catalog and schema from the alerts
 * API and shares the Alerts page's own filter builder and Slack picker.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Slack, Webhook, Power, Pause, Check } from "lucide-react";
import axiosInstance from "shared/services/api/axiosInstance";
import BrandDropdown from "shared/components/ui/BrandDropdown";
import AlertFilters from "features/alerts/components/AlertFilters";
import SlackChannelPicker from "features/agents/WorkflowEditor/SlackChannelPicker";
import { getAlertCatalog, getAlertSchema, setAlertEnabled } from "features/alerts/api/alertsApi";
import { getSlackConnection, getSlackChannels } from "features/agents/api/agentsApi";

const INPUT = "h-8 rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] outline-none transition-colors focus:border-[color:var(--text-primary)]";
const LABEL = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-secondary)]";
const UNITS = [{ value: "60", label: "minutes" }, { value: "3600", label: "hours" }, { value: "86400", label: "days" }];
const VARS = ["name", "measure", "value", "operator", "threshold", "window", "count", "link"];

/** 900 → { n: 15, unit: "60" }; the largest unit that divides evenly. */
function splitWindow(seconds) {
  const s = Number(seconds) || 900;
  if (s % 86400 === 0) return { n: s / 86400, unit: "86400" };
  if (s % 3600 === 0) return { n: s / 3600, unit: "3600" };
  return { n: Math.max(1, Math.round(s / 60)), unit: "60" };
}
const unitSuffix = (unit) => ({ ms: "ms", usd: "$", percent: "%" }[unit] || "");

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className={LABEL}>{label}</span>
      {children}
    </label>
  );
}

function Segmented({ value, onChange, options, ariaLabel, disabled }) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className="inline-flex rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-0.5">
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value} type="button" role="radio" aria-checked={on} disabled={disabled} onClick={() => onChange(o.value)}
            className={`inline-flex h-7 items-center gap-1.5 rounded px-3 text-[12px] font-medium transition-colors ${on ? "bg-[color:var(--surface-base)] text-[color:var(--text-primary)] shadow-token-sm" : "text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"}`}
          >
            {o.icon}{o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Slack, as a destination: checks the organization's connection, and when there is
 * none offers to make one from right here — the OAuth round trip returns to
 * this page, and the card re-checks when the window regains focus.
 */
export function SlackDestination({ teamId, value, onChange, disabled }) {
  const [conn, setConn] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  const check = useCallback(() => {
    getSlackConnection().then((c) => setConn(c || { connected: false })).catch(() => setConn({ connected: false }));
  }, []);
  useEffect(() => { check(); }, [check]);
  useEffect(() => {
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [check]);

  const connect = async () => {
    setStarting(true); setError(null);
    try {
      // Return to this very page, so the card is still here when Slack hands back.
      const return_to = `${window.location.pathname}${window.location.search || ""}`;
      const { data } = await axiosInstance.post("/integrations/slack/org/start", { return_to }, { params: { team_id: teamId } });
      if (data?.url) { window.location.href = data.url; return; }
      setError("Could not start Slack authorization.");
    } catch (e) {
      setError(e?.response?.data?.error || "Could not start Slack authorization.");
    } finally {
      setStarting(false);
    }
  };

  if (!conn) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 py-2 text-[12px] text-[color:var(--text-muted)]">
        <Loader2 size={13} className="animate-spin" /> Checking Slack…
      </div>
    );
  }
  if (!conn.connected || conn.needs_reconnect) {
    return (
      <div data-testid="slack-connect" className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 py-2.5">
        <div className="text-[13px] font-medium text-[color:var(--text-primary)]">
          {conn.needs_reconnect ? "Slack needs reconnecting." : "Slack is not connected for this organization."}
        </div>
        <p className="mt-0.5 text-[12px] leading-snug text-[color:var(--text-muted)]">
          Connect it and come back to pick a channel: the card will still be here. Or send this alert to a webhook instead.
        </p>
        {error && <p className="mt-1 text-[12px] text-[color:var(--feedback-error)]">{error}</p>}
        <button
          type="button" onClick={connect} disabled={starting || disabled}
          className="mt-2 inline-flex h-7 items-center gap-1.5 rounded-md bg-brand px-2.5 text-[12px] font-medium text-ink-onBrand hover:bg-brand-hover disabled:opacity-50"
        >
          {starting ? <Loader2 size={12} className="animate-spin" /> : <Slack size={12} />} {conn.needs_reconnect ? "Reconnect Slack" : "Connect Slack"}
        </button>
      </div>
    );
  }
  return (
    <SlackChannelPicker
      field={{ key: "channel", label: "Channel" }} nodeKind="slack_send_message" showHelp={false}
      value={value || ""} onChange={(v) => onChange(v)}
    />
  );
}

/** A channel id, shown as the channel's name once the list is in — an id is not something to read. */
function ChannelName({ id, name }) {
  const [label, setLabel] = useState(name ? `#${String(name).replace(/^#/, "")}` : null);
  useEffect(() => {
    if (label || !id) return undefined;
    let on = true;
    getSlackChannels().then((rows) => {
      const hit = (rows || []).find((c) => c.id === id);
      if (on && hit) setLabel(hit.is_im ? hit.name : `#${String(hit.name || "").replace(/^#/, "")}`);
    }).catch(() => {});
    return () => { on = false; };
  }, [id, label]);
  return <div className="text-[13px] text-[color:var(--text-primary)]">{label || id || "—"}</div>;
}

/**
 * @param {object} props.payload  the proposal's payload, merged with the reviewer's edits
 * @param {function} props.onChange  (patch) => void; absent once the card is decided
 */
export function AlertEditor({ payload, teamId, onChange }) {
  const p = payload || {};
  const readOnly = !onChange;
  const set = (patch) => { if (onChange) onChange(patch); };
  const [catalog, setCatalog] = useState(null);
  const [schema, setSchema] = useState(null);
  const [sendTab, setSendTab] = useState(p.webhook_url && !p.slack_channel_id ? "webhook" : "slack");
  const [win, setWin] = useState(() => splitWindow(p.window_seconds));
  // The eval an eval alert was proposed for; kept so switching away and back does not lose it.
  const [proposedEval] = useState(() => (p.eval_lineage_id ? { id: p.eval_lineage_id, name: p.eval_name || "Eval" } : null));

  useEffect(() => { getAlertCatalog().then(setCatalog).catch(() => setCatalog({ measures: {}, aggregations: {}, operators: {}, windows: [] })); }, []);
  useEffect(() => { let on = true; getAlertSchema(p.scope || "run").then((s) => { if (on) setSchema(s); }).catch(() => {}); return () => { on = false; }; }, [p.scope]);

  const measures = useMemo(() => catalog?.measures?.[p.scope] || [], [catalog, p.scope]);
  const measure = measures.find((m) => m.key === p.measure) || null;
  const unit = measure?.unit || p.unit || "count";
  const aggOptions = (measure?.aggregations || [p.aggregation]).filter(Boolean).map((a) => ({ value: a, label: catalog?.aggregations?.[a] || a }));
  const opOptions = Object.entries(catalog?.operators || { [p.operator]: { label: p.operator } }).map(([k, o]) => ({ value: k, label: o.label }));

  const setWindow = (next) => {
    setWin(next);
    const seconds = Math.max(60, Math.round(Number(next.n) || 0) * Number(next.unit));
    set({ window_seconds: seconds });
  };
  const changeScope = (scope) => {
    if (scope === p.scope) return;
    const first = catalog?.measures?.[scope]?.[0];
    // A span has no tool-call count and a run has no model: the measure and
    // the filter are about the old scope, so they start over with it.
    set({
      scope, measure: first?.key || ({ run: "runs", span: "spans", eval: "failures" }[scope]), aggregation: first?.aggregations?.[0] || "count", filter: { groups: [] },
      eval_lineage_id: scope === "eval" && proposedEval ? proposedEval.id : null,
      eval_name: scope === "eval" && proposedEval ? proposedEval.name : null,
    });
  };
  const changeMeasure = (key) => {
    const m = measures.find((x) => x.key === key);
    set({ measure: key, aggregation: m?.aggregations?.includes(p.aggregation) ? p.aggregation : (m?.aggregations?.[0] || "count") });
  };

  return (
    <div className="mt-3 grid gap-3" data-testid="alert-editor">
      <Field label="Name">
        <input className={`${INPUT} w-full`} value={p.name || ""} disabled={readOnly} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Checkout p95 latency" />
      </Field>

      <div className="flex flex-wrap items-end gap-2">
        <div>
          <span className={LABEL}>Scope</span>
          <Segmented ariaLabel="Scope" value={p.scope || "run"} disabled={readOnly} onChange={changeScope}
            options={[{ value: "run", label: "Run" }, { value: "span", label: "Span" }, ...(proposedEval ? [{ value: "eval", label: "Eval" }] : [])]} />
        </div>
        {p.scope === "eval" && (
          <Field label="Eval" className="min-w-[150px]">
            <div className={`${INPUT} flex w-full items-center truncate`} title="The eval this alert watches">{p.eval_name || proposedEval?.name || "Eval"}</div>
          </Field>
        )}
        <Field label="Measure" className="min-w-[150px] flex-1">
          <BrandDropdown value={p.measure} onChange={changeMeasure} disabled={readOnly}
            options={measures.length ? measures.map((m) => ({ value: m.key, label: m.label })) : [{ value: p.measure, label: p.measure }]} />
        </Field>
        <Field label="Aggregation" className="min-w-[120px]">
          <BrandDropdown value={p.aggregation} onChange={(v) => set({ aggregation: v })} options={aggOptions} disabled={readOnly} />
        </Field>
      </div>

      <div>
        <span className={LABEL}>Filters</span>
        {schema ? (
          <AlertFilters value={p.filter || { groups: [] }} onChange={(v) => set({ filter: v })} schema={schema} scope={p.scope || "run"}
            evalLineageId={p.scope === "eval" ? p.eval_lineage_id || null : null} />
        ) : (
          <div className="text-[12px] text-[color:var(--text-muted)]">Loading fields…</div>
        )}
      </div>

      <div>
        <span className={LABEL}>Condition</span>
        <div className="flex flex-wrap items-center gap-2 text-[13px] text-[color:var(--text-secondary)]">
          <span>Trigger when the value is</span>
          <span className="w-[130px]"><BrandDropdown value={p.operator} onChange={(v) => set({ operator: v })} options={opOptions} disabled={readOnly} /></span>
          <span className="relative inline-flex items-center">
            <input
              aria-label="Threshold" className={`${INPUT} w-[120px] pr-7 tabular-nums`} type="number" inputMode="decimal" step="any" disabled={readOnly}
              value={p.threshold ?? ""} onChange={(e) => set({ threshold: e.target.value === "" ? "" : Number(e.target.value) })}
            />
            {unitSuffix(unit) && <span className="pointer-events-none absolute right-2 text-[11px] text-[color:var(--text-muted)]">{unitSuffix(unit)}</span>}
          </span>
          <span>over the last</span>
          <input aria-label="Window" className={`${INPUT} w-[64px] tabular-nums`} type="number" min={1} disabled={readOnly}
            value={win.n} onChange={(e) => setWindow({ ...win, n: e.target.value })} />
          <span className="w-[110px]"><BrandDropdown value={win.unit} onChange={(u) => setWindow({ ...win, unit: u })} options={UNITS} disabled={readOnly} /></span>
        </div>
        {p.aggregation === "rate" && catalog?.rate_min_sample > 1 && (
          <p className="mt-1.5 text-[12px] text-[color:var(--text-muted)]">
            Needs at least {catalog.rate_min_sample} {p.scope === "eval" ? "decisions" : `${p.scope}s`} in the window — {p.scope === "eval" ? "one result on its own is 0% or 100%" : "a single failure is 100%"}.
          </p>
        )}
      </div>

      <Field label="Message (optional)">
        <textarea
          className="w-full rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 py-2 font-mono text-[12px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--text-primary)]"
          rows={3} disabled={readOnly} value={p.message || ""} onChange={(e) => set({ message: e.target.value })}
          placeholder={"Leave blank for the default: name, value vs threshold, window, and a link."}
        />
        {!readOnly && (
          <div className="mt-1 flex flex-wrap gap-1">
            {VARS.map((v) => (
              <button key={v} type="button" onClick={() => set({ message: `${p.message || ""}{{${v}}}` })}
                className="rounded border border-[color:var(--border-default)] px-1.5 py-0.5 font-mono text-[11px] text-[color:var(--text-secondary)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
                {`{{${v}}}`}
              </button>
            ))}
          </div>
        )}
      </Field>

      <div>
        <span className={LABEL}>Send to</span>
        <Segmented ariaLabel="Send to" value={sendTab} disabled={readOnly} onChange={setSendTab}
          options={[{ value: "slack", label: "Slack", icon: <Slack size={12} /> }, { value: "webhook", label: "Webhook", icon: <Webhook size={12} /> }]} />
        <div className="mt-2">
          {sendTab === "slack" ? (
            readOnly
              ? <ChannelName id={p.slack_channel_id} name={p.slack_channel_name} />
              : <SlackDestination teamId={teamId} value={p.slack_channel_id} disabled={readOnly} onChange={(id) => set({ slack_channel_id: id || null, slack_channel_name: null })} />
          ) : (
            <Field label="URL">
              <input className={`${INPUT} w-full`} disabled={readOnly} value={p.webhook_url || ""} onChange={(e) => set({ webhook_url: e.target.value || null })} placeholder="https://" />
            </Field>
          )}
        </div>
      </div>

      <label className="inline-flex items-center gap-2 text-[13px] text-[color:var(--text-primary)]">
        <input type="checkbox" className="accent-[color:var(--trodo-primary)]" disabled={readOnly} checked={p.enabled === true} onChange={(e) => set({ enabled: e.target.checked })} />
        Switch it on as soon as it is created
      </label>
    </div>
  );
}

/** Whether the card can be approved as it stands: an alert needs somewhere to send to. */
export function alertCannotApprove(payload) {
  const p = payload || {};
  return !(p.slack_channel_id || p.webhook_url);
}

/**
 * After approval: the alert exists, with its state, and the one control a
 * person wants right here — on or off — calling the alerts API the Alerts
 * page itself uses.
 */
export function AlertPostApproval({ alertId, enabled, teamSlug, name }) {
  const [on, setOn] = useState(!!enabled);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const toggle = async () => {
    setBusy(true); setError(null);
    try { const a = await setAlertEnabled(alertId, !on); setOn(!!a?.enabled); }
    catch (e) { setError(e?.response?.data?.error || "Could not update the alert."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px]">
      <span className="inline-flex items-center gap-1.5 text-[color:var(--text-secondary)]">
        <Check size={12} /> {name ? `"${name}" ` : ""}{on ? "is on and evaluating every minute." : "is created, switched off."}
      </span>
      <button type="button" disabled={busy} onClick={toggle}
        className="inline-flex h-7 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] px-2.5 text-[12px] font-medium text-[color:var(--text-primary)] hover:bg-surface-hover disabled:opacity-50">
        {on ? <Pause size={12} /> : <Power size={12} />} {on ? "Pause" : "Turn on"}
      </button>
      <Link to={`/${encodeURIComponent(teamSlug || "")}/alerts/${encodeURIComponent(alertId)}`} className="text-[color:var(--trodo-primary)]">Open the alert</Link>
      {error && <span className="text-[color:var(--feedback-error)]">{error}</span>}
    </div>
  );
}
